import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isValidCron } from 'cron-validator'
import { quartzActions } from '../../../actions/quartzActions';
import quartzApi from '../../../api/QuartzApi';
import ExecuteBox from '../../common/core/ExecuteBox';
import CheckBox from '../../common/core/CheckBox';
import { getValueByInputType } from '../../../shared/scripts';
import { Loader } from '../../common/loader/Loader';
import * as quartzSelectors from './selectors';
import { BonTableCellText } from '../../common/bonusTable/BonTable';
import BonForm from '../../common/bonusForm/BonusForm';
import Button from '../../common/Button';
import { FaPlusSquare, FaTrashAlt } from 'react-icons/fa';
import environmentEnum from '../../../shared/enums/environmentEnum';
import * as commonSelectors from '../../common/selectors';
import './quartzJobs.css';
import '../../../styles/Configuration.css';


export const QuartzJobInner = ({
    index,
    envName,
    jobName,
    readOnly = true,
}) => {
    const [job, setJob] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [currentEnvName, setCurrentEnvName] = useState("");

    const [loading, setLoading] = useState(false);
    const [triggersCount, setTriggersCount] = useState(0);
    const [activeTriggersCount, setActiveTriggersCount] = useState(0);

    const environmentSelector = useSelector(commonSelectors.environment);
    const expandedJobSelector = useSelector(quartzSelectors.expandedJob);
    const jobSelector = useSelector(quartzSelectors.environmentJobTriggers(envName, jobName));

    const dispatch = useDispatch();

    useEffect(() => {
        const getEnvName = () => {
            if (environmentSelector === environmentEnum.DEV) {
                return environmentEnum.DEV;
            }
            if (environmentSelector === environmentEnum.LOCALDEV) {
                return environmentEnum.LOCALDEV;
            }

            return environmentSelector;
        }

        setCurrentEnvName(getEnvName);
    }, [environmentSelector]);

    useEffect(() => {
        const isExpanded = expandedJobSelector.includes(index);
        if (isExpanded !== expanded) {
            setExpanded(isExpanded);
        }

    }, [expandedJobSelector, index, expanded]);

    useEffect(() => {
        const _job = JSON.parse(JSON.stringify(jobSelector));
        setJob(_job);
    }, [jobSelector]);

    useEffect(() => {
        function isTriggerActive(cron) {
            if (cron == null) {
                return false;
            }
            if (!isValidCron(cron)) {
                return false;
            }
            const splits = cron.trim().split(/\s+/);
            if (splits.length === 7) {
                const match = splits[6].match(/(\d+)/i);
                if (match) {
                    const year = match[0];
                    if (year > new Date().getFullYear()) {
                        return false;
                    }
                }
            }
            return true;
        }

        const _triggers = job?.triggers || [];
        setTriggersCount(_triggers.length);
        const activeTriggersCount = _triggers.filter(t => isTriggerActive(t.cron)).length;
        setActiveTriggersCount(activeTriggersCount);
    }, [job]);

    const handleTriggerChange = (event, trigger) => {
        const field = event.target.name;
        trigger[field] = getValueByInputType(event);
        const isEdited = trigger.isEdited;
        if (isEdited === false && trigger.isNew !== true && field !== "isSelected") {
            trigger.isEdited = true;
            dispatch(
                quartzActions.handleTriggerChange(trigger, 2, isEdited, [])
            );
        }

        dispatch(
            quartzActions.setJob(job, envName)
        );
    }

    const addTrigger = () => {
        setLoading(true);
        quartzApi
            .getNewTriggersId(1)
            .then(data => {
                const trigger = Object.assign({}, {
                    cron: "",
                    tenant: "",
                    jobType: job.jobType,
                    environment: envName,
                    jobName: job.jobName,
                    class: job.class,
                    isNew: true,
                    id: data.ids[0],
                    isSelected: false
                });

                dispatch(
                    quartzActions.handleTriggerChange(trigger, 0, false, [])
                );

                job.triggers.push(trigger);
                dispatch(
                    quartzActions.setJob(job, envName)
                );

                setLoading(false);
            });
    }

    const deleteTrigger = () => {
        setLoading(true);

        const triggers = job.triggers;
        const notSelected = triggers.filter(x => x.isSelected === false);
        if (notSelected.length === triggers.length) {
            setLoading(false);
            return;
        }

        dispatch(
            quartzActions.handleTriggerChange({}, 1, true, triggers)
        );

        job.triggers = notSelected;
        dispatch(
            quartzActions.setJob(job, envName)
        );

        setLoading(false);
    }

    const handleRunJobBtnClick = tenants => {
        dispatch(
            quartzActions.runJob(job.class, prepareTenantsString(tenants))
        )
    }

    function prepareTenantsString(string) {
        return string.replace(/(\w+)\W*/g, '$1,');
    }

    function isValidCron(cronexpression) {
        return isCronValid(cronexpression);
    }

    return (
        <div className="bon-job">
            {job != null &&
                <BonForm.ExpansionPanel
                    nameId={`${job.jobName}-${envName}-${index}`}
                    label={
                        <span className='job-caption'>
                            <span className='job-name'>
                                {job.jobName}
                            </span>
                            <span className='base-info'>
                                <span
                                    className='job-type'
                                    title='JobType'
                                >
                                    {`${job.jobType}`}
                                </span>
                                <span className='counts-info'>
                                    <span>
                                        ActiveTriggers:&nbsp;<strong style={{ color: activeTriggersCount > 0 ? 'green' : 'red' }}>{activeTriggersCount}</strong>
                                    </span>
                                    <span>
                                        CountTriggers:&nbsp;<strong style={{ color: triggersCount > 0 ? 'lightgray' : 'red' }}>{triggersCount}</strong>
                                    </span>
                                </span>
                            </span>
                        </span>
                    }
                    expandFunc={() => expanded}
                    onExpand={() => {
                        dispatch(
                            quartzActions.setExpandedJob(index)
                        );
                    }}
                >
                    {
                        renderJobDetail()
                    }
                </BonForm.ExpansionPanel>
            }
            <Loader
                isloading={loading}
            />
        </div>
    );

    function renderJobDetail() {
        const isSystemJob = job.jobType === "SystemJob" ?? false;

        return (expanded
            ? <div style={{ width: '100%' }}>
                <table className="global-table">
                    <thead>
                        <tr className="global-table-header grey-text">
                            <th style={{ width: "10%" }}><span></span></th>
                            <th
                                className="grey-text"
                                style={{ width: "55%" }}
                            >
                                <span>Tenants</span>
                            </th>
                            <th
                                className="grey-text"
                                style={{ width: "35%" }}
                            >
                                <span>Cron</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {job.triggers.length < 1
                            ? <tr>
                                <td></td>
                                <td className="no-row" colSpan="2"><span>No triggers found ...</span></td>
                            </tr>
                            : job.triggers
                                .map((trigger, _index) => {
                                    const {
                                        cron,
                                        tenant,
                                        id,
                                        isSelected,
                                        class: _class,
                                        environment,
                                    } = trigger;

                                    const _isCronValid = isValidCron(cron);

                                    return (
                                        <tr key={`${id}-${environment}-${_class}`}>
                                            <td>
                                                <div
                                                    style={{
                                                        textAlign: 'center',
                                                        alignItems: 'center',
                                                        marginTop: 6,
                                                    }}
                                                >
                                                    <CheckBox
                                                        name={job.class + "-" + tenant}
                                                        isChecked={isSelected}
                                                        isDisabled={readOnly}
                                                        onChange={bool => handleTriggerChange(
                                                            { target: { name: "isSelected", checked: bool, type: "boolean" } }
                                                            , trigger
                                                        )}
                                                    />
                                                </div>
                                            </td>
                                            <BonTableCellText
                                                name="Tenant"
                                                placeholder='Tenant'
                                                value={isSystemJob ? "" : tenant}
                                                onChange={str => handleTriggerChange(
                                                    {
                                                        target: {
                                                            name: "tenant",
                                                            value: str.toUpperCase(),
                                                            type: "text",
                                                        }
                                                    }
                                                    , trigger)}
                                                readOnly={isSystemJob || readOnly}
                                                inputClassName="bg-dark grey-text form-control"
                                            />
                                            <BonTableCellText
                                                name="Cron"
                                                placeholder='Cron'
                                                value={cron}
                                                onChange={str => handleTriggerChange(
                                                    {
                                                        target: {
                                                            name: "cron",
                                                            value: str,
                                                            type: "text",
                                                        }
                                                    }
                                                    , trigger)}
                                                readOnly={readOnly}
                                                inputClassName={"bg-dark grey-text form-control" + (_isCronValid ? " is-valid" : " is-invalid")}
                                            />
                                        </tr>
                                    )
                                })
                        }
                    </tbody>
                </table>
                <div className='tool-box'>
                    <div
                        className="button-items"
                        style={{ marginBottom: '10px' }}
                    >
                        <Button
                            text="Add"
                            icon={<FaPlusSquare />}
                            disabled={readOnly}
                            onClick={() => addTrigger()}
                        />
                        <Button
                            text="Delete"
                            icon={<FaTrashAlt />}
                            disabled={readOnly}
                            onClick={() => deleteTrigger()}
                        />
                    </div>
                    {currentEnvName == envName &&
                        <ExecuteBox
                            captionRender={
                                () => <span>
                                    {`Manual run${isSystemJob ? '' : ':'}`}
                                </span>
                            }
                            inputDefaultText='for tenants...'
                            onClick={handleRunJobBtnClick}
                            disabled={readOnly}
                            showInput={!isSystemJob}
                        />
                    }
                </div>
            </div>
            : <div />
        );
    }
}

export function isCronValid(cronExpression) {
    const splits = cronExpression.trim().split(/\s+/);
    if (splits.length === 7) {
        const match = splits[6].match(/(\d+)/i);
        if (match) {
            const year = match[0];
            if (year < 1970 || year > 2099) {
                return false;
            } else {
                return isValidCron(cronExpression.replace(year, ""), { seconds: true, alias: true, allowBlankDay: true });
            }
        } else {
            return isValidCron(cronExpression, { seconds: true, alias: true, allowBlankDay: true });
        }
    }
    return isValidCron(cronExpression, { seconds: true, alias: true, allowBlankDay: true });
}


export default QuartzJobInner;